import {
    DependencyList,
    useEffect,
    useRef,
    useMemo,
    MutableRefObject,
} from 'react';
import isEqual from 'lodash/isEqual';

function debounce<F extends (...arg: unknown[]) => void>(
    timeOutRef: MutableRefObject<number | null>,
    fun: F,
    delay: number,
) {
    return (...args: Parameters<F>) => {
        if (timeOutRef.current) {
            window.clearTimeout(timeOutRef.current);
        }
        timeOutRef.current = window.setTimeout(() => {
            fun(...args);
        }, delay);
    };
}

/**
 * Debounce the call to `fun` for `delay` milliseconds.
 *
 * @param {function} fun - The function to debounce.
 * @param {number} delay - The debounce delay in milliseconds.
 */
export const useDebounce = <F extends (...arg: unknown[]) => void>(
    fun: F,
    delay: number,
) => {
    const timeOutRef = useRef<number | null>(null);

    const wrapped = debounce<F>(timeOutRef, fun, delay);

    return wrapped;
};

/**
 * Debounce the call to `fun` for `delay` milliseconds and make it cancellable.
 *
 * @param {function} fun - The function to debounce.
 * @param {number} delay - The debounce delay in milliseconds.
 */
export const useCancellableDebounce = <F extends (...args: unknown[]) => void>(
    fun: F,
    delay: number,
) => {
    const timeOutRef = useRef<number | null>(null);

    const debouncedFunction = debounce<F>(timeOutRef, fun, delay);

    const cancel = () => {
        if (timeOutRef.current) {
            window.clearTimeout(timeOutRef.current);
            timeOutRef.current = null;
        }
    };

    return { debouncedFunction, cancel };
};

/**
 * Returns a ref to be used on elements where we want to disable mouse wheel events.
 */
export const useScrollessRef = () => {
    // We need this ref and and the event listeners to be able to disable
    // mouse wheel events
    const ref = useRef<HTMLInputElement | null>(null);
    useEffect(() => {
        const preventScroll: EventListener = (e) => e.preventDefault();
        ref.current?.addEventListener('wheel', preventScroll);

        return () => {
            ref.current?.removeEventListener('wheel', preventScroll);
        };
    }, [ref.current]);
    return ref;
};

/**
 * Store a list of dependency in a ref and only update it when a deep compare find
 * that they are different.
 * This is used for `useDeepMemo`.
 *
 * @param {DependencyList} value - The list of dependency.
 */
const useMemoizeDeepCompare = (value: DependencyList): DependencyList => {
    const ref = useRef<DependencyList>([]);
    if (!isEqual(value, ref.current)) {
        ref.current = value;
    }
    return ref.current;
};

/**
 * A deep-compare version of React's `useMemo`.
 * It work by using the hook `useMemoizeDeepCompare`, which put the dependency list in a ref,
 * and only update the ref when the value is not equal using a deep compare.
 * Since `useMemo` use `Object.is` for the dependency, it will not trigger as long as the ref value is
 * not updated, since ref keeps the same instance.
 *
 * Take the same argument has React's `useMemo`:
 * @param {function} factory - A function returning the value to memoize.
 * @param {DependencyList} dependencies - A list of dependencies.
 */
export const useDeepMemo = <T>(
    factory: () => T,
    dependencies: DependencyList,
) => useMemo(factory, useMemoizeDeepCompare(dependencies));
